import { Inject, Injectable, NgZone } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { EMPTY, Observable, from, throwError, timer, of } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import {
  map,
  catchError,
  timeout,
  takeUntil,
  switchMap,
  retryWhen,
  concatMap
} from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '@portal/environments/environment';
import { MessageService } from 'primeng/api';
import {
  getIdToken,
  getOktaIdTokenObj,
  isLoginInProgress,
  getReturnUrl
} from '@portal/app/shared/helpers/helpers';
import { ApiService } from '@portal/app/shared/services/api.service';
import { ServerErrorService } from '@portal/app/shared/services/server-error.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { LocalStorageService } from '@portal/app/shared/services/localstorage.service';
import { Location } from '@angular/common';

const DEFAULT_TIMEOUT_MS = 60000; // 1 minute
const LONG_RUNNING_TIMEOUT_MS = 180000; // 3 minutes

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshQueue: {
    request: HttpRequest<unknown>;
    next: HttpHandler;
    subscriber: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }[] = [];

  constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly messageService: MessageService,
    private readonly apiService: ApiService,
    private readonly localStorageService: LocalStorageService,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly serverErrorService: ServerErrorService,
    private readonly location: Location
  ) {}

  /**
   * Redirect to login page while preserving the current URL as returnUrl.
   * This ensures users return to their original page after re-authentication.
   */
  private redirectToLogin(): void {
    const returnUrl = getReturnUrl(this.location.path(true));

    this.router.navigate(['/login'], {
      queryParams: returnUrl ? { returnUrl } : {}
    });
  }

  static createErrorObject(message: string) {
    return {
      key: 'error',
      severity: 'error',
      summary: 'Error',
      detail: message
    };
  }

  /**
   * Creates retry logic for critical endpoints
   * @param maxRetries Maximum number of retry attempts
   * @param baseDelay Base delay in milliseconds (will be exponentially increased)
   */
  private createRetryLogic(maxRetries = 3, baseDelay = 1000) {
    return retryWhen<HttpEvent<unknown>>((errors) =>
      errors.pipe(
        concatMap((error, index) => {
          const retryAttempt = index + 1;

          if (
            retryAttempt <= maxRetries &&
            (((error as HttpErrorResponse).status >= 500 &&
              (error as HttpErrorResponse).status < 600) ||
              (error as HttpErrorResponse).status === 0)
          ) {
            const delay = baseDelay * Math.pow(2, index);
            return timer(delay);
          }

          return throwError(() => error);
        })
      )
    );
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const isTestingLogin = request.params.has('testLogin');
    if (this.shouldSkipAuthentication(request)) {
      return next.handle(request).pipe(
        takeUntil(this.apiService.pendingRequestCancellationStatus),
        timeout(this.getTimeoutForRequest(request)),
        map((event) => event),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
    }

    // If token refresh is already in progress, queue this request immediately
    if (this.isRefreshing && !isTestingLogin) {
      return new Observable<HttpEvent<unknown>>((subscriber) => {
        this.refreshQueue.push({ request, next, subscriber });
      });
    }

    return from(getIdToken(this.oktaAuth)).pipe(
      switchMap(async (initialToken) => {
        const authToken =
          !environment.production &&
          (!environment.innovation ||
            (environment.innovation &&
              environment.apiDomain?.includes('localhost'))) &&
          environment.apiDomain?.includes('localhost') &&
          environment.authToken
            ? environment.authToken
            : initialToken;

        if (!authToken) {
          if (isLoginInProgress()) {
            this.redirectToLogin();
            return null;
          }

          this.oktaAuth.tokenManager.clear();
          localStorage.removeItem('okta-token-storage');
          localStorage.removeItem('user');
          this.redirectToLogin();
          return null;
        }

        return this.addTokenToRequest(request, authToken);
      }),
      switchMap((updatedRequest) => {
        if (!updatedRequest) return EMPTY;

        const requestPipeline = next.handle(updatedRequest);

        if (updatedRequest.url.includes('/user/permissions')) {
          return requestPipeline.pipe(this.createRetryLogic(3, 1000));
        }

        return requestPipeline;
      }),
      takeUntil(this.apiService.pendingRequestCancellationStatus),
      timeout(this.getTimeoutForRequest(request)),
      map((event) => event),
      catchError((error: HttpErrorResponse) => {
        if (!isTestingLogin) {
          console.error('error', error);

          if (error.status === 401) {
            return this.handle401Error(request, next);
          }

          if (
            (error.status >= 500 && error.status < 600) ||
            error.status === 0
          ) {
            if (request.url.includes('/user/permissions')) {
              this.ngZone.run(() => {
                this.serverErrorService.setServerError(true);
              });

              const fallbackResponse = new HttpResponse({
                status: 200,
                body: { clients: [], permissions: [] },
                url: request.url
              });

              return of(fallbackResponse);
            }
            return throwError(() => error);
          }

          // Show elasticsearch error
          if (
            error &&
            error.error &&
            error.error.message &&
            error.error.errorCode === 'ResponseError'
          ) {
            this.ngZone.run(() => {
              this.messageService.add(
                AuthenticationInterceptor.createErrorObject(error.error.message)
              );
            });
            return throwError(() => error);
          }
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return from(getOktaIdTokenObj(this.oktaAuth)).pipe(
      switchMap(async (currentToken) => {
        if (
          !currentToken ||
          this.oktaAuth.tokenManager.hasExpired(currentToken)
        ) {
          return this.attemptTokenRefresh(request, next);
        } else {
          this.redirectToLogin();
          return EMPTY;
        }
      }),
      switchMap((result) => result)
    );
  }

  private attemptTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      return from(this.oktaAuth.tokenManager.renew('idToken')).pipe(
        switchMap((renewedToken) => {
          const idToken = renewedToken as any; // eslint-disable-line @typescript-eslint/no-explicit-any
          if (idToken?.idToken) {
            this.isRefreshing = false;

            this.refreshQueue.forEach((queuedReq) => {
              const newRequest = this.addTokenToRequest(
                queuedReq.request,
                idToken.idToken
              );
              queuedReq.next.handle(newRequest).subscribe({
                next: (res) => queuedReq.subscriber.next(res),
                error: (err) => queuedReq.subscriber.error(err),
                complete: () => queuedReq.subscriber.complete()
              });
            });
            this.refreshQueue = [];

            const retryRequest = this.addTokenToRequest(
              request,
              idToken.idToken
            );
            return next.handle(retryRequest);
          } else {
            return throwError(
              () => new Error('Unable to refresh token - no token returned')
            );
          }
        }),
        catchError(() => {
          this.isRefreshing = false;

          this.refreshQueue.forEach((queuedReq) => {
            queuedReq.subscriber.complete();
          });
          this.refreshQueue = [];

          if (!isLoginInProgress()) {
            this.oktaAuth.tokenManager.clear();
            localStorage.removeItem('okta-token-storage');
            localStorage.removeItem('user');
          }
          this.redirectToLogin();
          return EMPTY;
        })
      );
    } else {
      return new Observable<HttpEvent<unknown>>((subscriber) => {
        this.refreshQueue.push({ request, next, subscriber });
      });
    }
  }

  private addTokenToRequest(
    request: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return request.clone({
      headers: request.headers
        .set('Accept', 'application/json')
        .set('provider', environment.ssoProvider)
        .set('Authorization', `Bearer ${token}`)
        .set('X-Page-UUID', this.localStorageService.getPageUUID())
    });
  }

  /**
   * List of API endpoints that should not require authentication
   */
  private readonly PUBLIC_API_ENDPOINTS = [
    `${environment.apiV1}/vendor/oauth/init/shopify`
  ];

  /**
   * Check if this API endpoint should skip authentication
   */
  private shouldSkipAuthentication(request: HttpRequest<unknown>): boolean {
    const url = request.url;

    return this.PUBLIC_API_ENDPOINTS.some((endpoint) => url.includes(endpoint));
  }

  /**
   * Get timeout duration for a request
   * Geo test creation and MIM export endpoints get 3 minutes, others get 1 minute
   * TODO: DU: Remove this once we have a better way to handle timeouts
   */
  private getTimeoutForRequest(request: HttpRequest<unknown>): number {
    const { url, method } = request;

    // Geo test creation can take longer due to multi-KPI tests
    const isGeoTestWrite =
      (url.includes('/geo-tests') || url.includes('/geo/tests')) &&
      (method === 'POST' || method === 'PATCH');

    // MIM bulk export counts the rows in the warehouse before responding
    const isMimExportRequest =
      url.includes('/results-preview/export-request') && method === 'POST';

    return isGeoTestWrite || isMimExportRequest
      ? LONG_RUNNING_TIMEOUT_MS
      : DEFAULT_TIMEOUT_MS;
  }
}
